import {Alert, Box, Button, Group, Loader, Paper, Text} from '@mantine/core'
import {IconArrowDownRight, IconArrowUpRight} from '@tabler/icons-react'

import {trendDownColor, trendUpColor} from 'src/theme/semantic-colors'

import type {DashboardStat} from './types'

type DashboardStatCardProps = {
    stat: DashboardStatCardData
}

function formatComparison({value, format}: StatComparison): string {
    if (format === 'absolute') {
        return value > 0 ? `+${value}` : String(value)
    }

    if (value > 0) {
        return `+${value}%`
    }

    return `${value}%`
}

export function DashboardStatCard({stat}: DashboardStatCardProps) {
    if (stat.isLoading) {
        return (
            <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    {stat.title}
                </Text>
                <Group justify="center" py="lg">
                    <Loader size="sm" />
                </Group>
            </Paper>
        )
    }

    if (stat.isError) {
        const message = stat.error instanceof Error ? stat.error.message : 'Failed to load stat'
        return (
            <Paper withBorder p="md" radius="md">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    {stat.title}
                </Text>
                <Alert color="red" mt="md" title="Could not load">
                    {message}
                </Alert>
                {stat.onRetry && (
                    <Button mt="sm" onClick={stat.onRetry} variant="light" size="xs">
                        Retry
                    </Button>
                )}
            </Paper>
        )
    }

    const showComparison = stat.comparison != null
    const comparisonValue = stat.comparison?.value ?? 0
    const isPositive = showComparison && comparisonValue > 0
    const isNegative = showComparison && comparisonValue < 0
    const DiffIcon = isPositive ? IconArrowUpRight : IconArrowDownRight
    const diffColor = isPositive ? trendUpColor() : trendDownColor()

    return (
        <Paper withBorder p="md" radius="md">
            <Group justify="space-between" align="flex-start">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    {stat.title}
                </Text>
            </Group>

            <Group align="baseline" gap="xs" mt="lg" wrap="wrap">
                <Text size="xl" fw={700} lh={1}>
                    {stat.value}
                </Text>
                {showComparison && stat.comparison && (
                    <>
                        <Text span fz="sm" fw={700} c={diffColor} lh={1}>
                            {formatComparison(stat.comparison)}
                            {(isPositive || isNegative) && (
                                <Box component="span" lh={0} style={{display: 'inline-flex'}}>
                                    <DiffIcon
                                        size={14}
                                        color={`var(--mantine-color-${diffColor}-6)`}
                                        stroke={1.75}
                                    />
                                </Box>
                            )}
                        </Text>
                        {stat.comparisonLabel && (
                            <Text span fz="xs" c="dimmed" lh={1}>
                                {stat.comparisonLabel}
                            </Text>
                        )}
                    </>
                )}
            </Group>
        </Paper>
    )
}
